import React, { useCallback, useContext, useMemo } from 'react'

import { fit } from 'fit-to-bounds'
import styled, { css } from 'styled-components'
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill'
import { ApplicationDataFull, ApplicationDataLuminary } from '../../types/ApplicationData'
import { SliderContext } from '../components/slider/Slider'
import { breakpoint } from '../style'
import { useDispatch } from '../store/hooks'
import { useCanInsertModule } from '../store/hooks/configurationHooks'
import ServicesList from '../components/ServicesList'
import { isServiceData, isSupportingColumnData, isVariableModule } from '../../types/ModuleData'
import { useAvailableModules } from '../store/hooks/dataHooks'
import { ConstelaConfiguration, ConstelaConfigurationModule } from '../../types/ConstelaConfiguration'
import { setModulesAction } from '../store/actions/configurationActions'
import { isDefined } from '../utils/typeGuards'
import LuminaryEditor, { LuminaryEditorProps } from '../components/preview/LuminaryEditor'

const Wrap = styled.div<{ steleLeft: number }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 1;

  ${ServicesList} {
    position: absolute;
    bottom: 200px;
    margin-right: 10px;
    left: ${({ steleLeft }) => steleLeft}px;
    transform: translateX(-100%);
  }
`

const SteleWrap = styled.div<{
  bottom: number
  center: number
}>`
  position: absolute;
  transform: translateX(-50%);

  ${(props) => css`
    left: ${props.center}px;
    bottom: ${props.bottom}px;
  `}
`

export type StageProps = {
  application: ApplicationDataFull
  configuration: ConstelaConfiguration
}

const Stele: React.FC<{
  application: ApplicationDataFull
  luminary: ApplicationDataLuminary
  configuration: ConstelaConfiguration
  screenScale: number
}> = ({ application, luminary: { bottom, center, scale: luminaryScale }, configuration, screenScale }) => {
  const scale = luminaryScale * screenScale
  const dispatch = useDispatch()

  const { insideSlider, currentSlide } = useContext(SliderContext)
  const canInsertModule = useCanInsertModule(application, configuration)
  const availableModules = useAvailableModules(application.id, configuration.type)

  const updateModules = useCallback<LuminaryEditorProps['updateModules']>(
    (nextModules: ConstelaConfigurationModule[], type, [firstIndex]) => {
      dispatch(
        setModulesAction(
          configuration.type === 'expert'
            ? {
                mode: 'expert'
              }
            : {
                mode: 'inspiration',
                applicationId: application.id
              },
          nextModules
        )
      )
      if (
        configuration.type === 'expert' &&
        type === 'insert' &&
        isDefined(firstIndex) &&
        availableModules.filter(isVariableModule).find((x) => x.id === nextModules[firstIndex].id)?.propertyMenu?.length
      ) {
        dispatch({
          type: 'UI/Expert/ShowPropertyOverlay',
          payload: {
            module: firstIndex
          }
        })
      }
    },
    [application.id, availableModules, configuration.type, dispatch]
  )

  return (
    <SteleWrap
      bottom={bottom * screenScale}
      center={screenScale * (typeof center === 'number' ? center : application.backgroundImage.width / 2)}
    >
      <LuminaryEditor
        availableModules={availableModules}
        canInsertModule={canInsertModule}
        updateModules={updateModules}
        configuration={configuration}
        scale={scale}
        animate={insideSlider && !currentSlide ? 'hide' : 'show'}
      />
    </SteleWrap>
  )
}

const Stage: React.FC<StageProps> = ({ application, configuration }) => {
  const { width = 0, height = 0, ref } = useResizeDetector()
  const focusX = width >= breakpoint.laptop ? 0.5 : 0.57
  const { offset, size, scale } = useMemo(
    () =>
      fit('cover', { width, height }, application.backgroundImage, {
        focusPoint: { x: focusX, y: 0.3 }
      }),
    [width, height, application.backgroundImage, focusX]
  )
  const contentDivStyle = useMemo<React.CSSProperties>(
    () => ({
      position: 'absolute',
      top: offset.top + 'px',
      left: offset.left + 'px',
      width: size.width + 'px',
      height: size.height + 'px'
    }),
    [offset.top, offset.left, size.width, size.height]
  )
  const availableModules = useAvailableModules(application.id, configuration.type)
  const steleLeft = useMemo(() => {
    const luminary = application.luminaries[application.luminaries.length - 1]
    const center = (luminary?.center ? luminary.center : application.backgroundImage.width * 0.5) * scale
    const column = availableModules.find(isSupportingColumnData)
    const luminaryWidth = (column?.width ?? 0) * scale * (luminary?.scale ?? 0)
    return center + offset.left - luminaryWidth
  }, [availableModules, application.backgroundImage.width, application.luminaries, offset.left, scale])

  const dispatch = useDispatch()
  const availableServices = useMemo(() => availableModules.filter(isServiceData), [availableModules])
  const setServices = useCallback(
    (nextServices: ConstelaConfigurationModule[]) => {
      dispatch({
        type: 'Configuration/SetServices',
        applicationId: application.id,
        services: nextServices
      })
    },
    [application.id, dispatch]
  )

  return (
    <Wrap ref={ref} steleLeft={steleLeft}>
      <div style={contentDivStyle}>
        {application.luminaries.map((luminary, id) => (
          <Stele
            application={application}
            configuration={configuration}
            luminary={luminary}
            key={id}
            screenScale={scale}
          />
        ))}
      </div>
      {configuration.type === 'inspiration' && (
        <ServicesList
          services={configuration.services}
          setServices={setServices}
          availableServices={availableServices}
        />
      )}
    </Wrap>
  )
}

export default Stage
